import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import type { TIrrigationDeviceStatus, TValuesCurrentPoints } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { apiFetch } from 'utils/apiFetch'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import type { TIrrigationActivityForPointOrZone } from '../../caches/StationValuesCache/StationValuesCache'
import type { TGetCacheUpdatesFromResponseParameters, TGetCacheUpdatesFromResponseReturn } from './_types'
import { createValuesMap } from './_utils/createValuesMap'
import { getPressureBadgeColors, getPressureBadgeStyle } from './_utils/getPressureBadgeStyle'
import { getPressureBaseZIndex } from './_utils/getPressureBaseZIndex'
import { getPressurePropertyItems } from './_utils/getPressurePropertyItems'
import { getPressureReading } from './_utils/getPressureReading'
import { getValueType } from './_utils/getValueType'
import { insertEmitterTypeForPressure } from './_utils/insertEmitterTypeForPressure'
import { insertEmitterTypeForStatus } from './_utils/insertEmitterTypeForStatus'
import { sortSelectedEmittersAndBestReadingsFirst } from './_utils/sortSelectedEmittersAndBestReadingsFirst'

type TStationItems =
  TGetCacheUpdatesFromResponseParameters['processedCaches']['stationValues']['itemsWithinView']

const getPressureAndStatusValueTypes = (emitterType: TEmitterType) => [
  insertEmitterTypeForPressure(emitterType),
  insertEmitterTypeForStatus(emitterType),
]

const makeApiArgs = ({
  propertyIds,
  selectedEmitters,
  stationItems,
}: {
  propertyIds: number[]
  selectedEmitters: TEmitterType[]
  stationItems: TStationItems
}): routes.ValuesCurrent.Request => {
  const returner: routes.ValuesCurrent.Request = {}

  if (stationItems.length) {
    const emitterTypes = new Set([
      ...selectedEmitters,
      ...stationItems.flatMap(({ meta }) => meta.emitterTypes),
    ])

    returner.points = {
      lngLats: stationItems.map(({ meta }) => meta.lngLat),
      values: createValuesMap([...emitterTypes].flatMap(getPressureAndStatusValueTypes)),
    }
  }

  if (propertyIds.length) {
    returner.properties = {
      propertyIds,
      values: createValuesMap(selectedEmitters.flatMap(getPressureAndStatusValueTypes)),
    }
  }

  return returner
}

export const getResponseAndShapeForCurrentPressure = async ({
  cacheKeys,
  processedCaches,
}: TGetCacheUpdatesFromResponseParameters): Promise<TGetCacheUpdatesFromResponseReturn> => {
  const stationItems = processedCaches.stationValues.itemsWithinView
  const propertyItems = processedCaches.propertyValues.itemsWithinView

  if (!stationItems.length && !propertyItems.length) return {}

  const { selectedEmitters } = mapControlsStore.getState()
  const properties = fieldAssetStore.getState().properties ?? {}

  const propertyIds = stationItems.length
    ? []
    : propertyItems
        .filter(({ id }) =>
          selectedEmitters.some((emitterType) =>
            properties[Number(id)]?.valuesCurrent.includes(insertEmitterTypeForPressure(emitterType)),
          ),
        )
        .map(({ id }) => Number(id))

  const args = makeApiArgs({ propertyIds, selectedEmitters, stationItems })

  const response: routes.ValuesCurrent.Response = Object.keys(args).length
    ? await apiFetch({ url: '/values-current', body: args })
    : {}

  const pointValueType = getValueType(MAP_VISUAL.POINT, 'irrigation_activity_pressure')
  const propertyValueType = getValueType(MAP_VISUAL.PROPERTY, 'irrigation_activity_pressure')
  const nowMs = Date.now()

  const itemsWithinViewThatNowHaveValues = stationItems.map(({ id, meta }) => {
    const point = properties[Number(meta.propertyId)]?.points?.[meta.lngLat]
    const responsePoint = response.points?.[meta.lngLat] ?? {}

    const emitterTypesWithPressure = meta.emitterTypes.filter((emitterType) =>
      point?.valuesCurrent.includes(insertEmitterTypeForPressure(emitterType)),
    )

    const unsortedRows = emitterTypesWithPressure.map((emitterType) => {
      const reading = getPressureReading({
        entry: responsePoint[insertEmitterTypeForPressure(emitterType)],
        nowMs,
      })

      const status = (responsePoint[insertEmitterTypeForStatus(emitterType) as TValuesCurrentPoints]?.value ??
        'unknown') as TIrrigationDeviceStatus

      return {
        emitterType,
        reading,
        status,
        displayValue: unitConverter.irrigationPressure(reading.psi).value(),
        isRunning: reading.state === 'LIVE' && status === 'on',
      }
    })

    const rows = sortSelectedEmittersAndBestReadingsFirst(unsortedRows, selectedEmitters)

    const valuesForPoint = rows.reduce<TIrrigationActivityForPointOrZone>(
      (acc, { emitterType, reading, status }) => {
        acc[`${meta.irrigationZoneId}|||${emitterType}`] = {
          status,
          duration: null,
          applied: null,
          pressure: reading,
          getBadgeColors: () => getPressureBadgeColors({ state: reading.state, status }),
        }

        return acc
      },
      {},
    )

    const badgeRow: (typeof rows)[number] | undefined = rows[0]
    const state = badgeRow?.reading.state ?? 'NONE'
    const status: TIrrigationDeviceStatus = badgeRow?.status ?? 'unknown'
    const isRunning = badgeRow?.isRunning ?? false
    const displayValue = badgeRow?.displayValue ?? null

    return {
      id: String(id),
      value: {
        [pointValueType]: {
          baseZIndex: getPressureBaseZIndex({ state, isRunning }),
          children: state === 'NONE' ? translate.phrases.templates('-') : displayValue,
          getContainerCSS: () => getPressureBadgeStyle({ state, status }),
          valuesForPoint,
        },
      },
    }
  })

  const returner: TGetCacheUpdatesFromResponseReturn = {
    properties: {
      itemsWithinViewThatNowHaveValues: getPressurePropertyItems({
        nowMs,
        properties,
        propertyItems,
        propertyValueType,
        response,
        selectedEmitters,
        showValues: !stationItems.length,
      }),
      cacheKey: cacheKeys.propertyCacheKey,
      itemIdsWithinView: processedCaches.propertyValues.itemIdsWithinView,
    },
  }

  if (stationItems.length) {
    returner.stations = {
      itemsWithinViewThatNowHaveValues,
      cacheKey: cacheKeys.stationCacheKey,
      itemIdsWithinView: stationItems.map(({ id }) => id),
    }
  }

  return returner
}
